import { useSeasonActions } from "./use-season-actions";
import { useContractHistory } from "./use-contract-history";
import { useAchievements } from "./use-achievements";
import { neutralFlightInput } from "./flight-input";
import { useShopActions } from "./use-shop-actions";
import { useSupplyActions } from "./use-supply-actions";
import type { SupplyControls, SupplyUseAck } from "@frontier/protocol/supplies";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Client } from "@colyseus/sdk";
import { PROTOCOL_VERSION } from "@frontier/protocol/client-compatibility";
import { PERSONAL_ROOM_NAME, activePersonalLocation } from "@frontier/protocol/world-location";
import type { Room } from "@colyseus/sdk";
import {
  ROOM_NAME,
  missionsViewSchema,
  discoveriesViewSchema,
  noticeSchema,
  partyViewSchema,
  profileSchema,
  publicEventsViewSchema,
  riskPrivateViewSchema,
} from "@frontier/protocol/contracts";
import type {
  ClientAction,
  ClientInput,
  DiscoveriesView,
  MissionView,
  PartyCommand,
  PartyView,
  Profile,
  PublicEventsView,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import { SectorState, toWorldSnapshot } from "@frontier/protocol/state";
import type { InputIntent } from "./game/mount-game";
import { ApiError, api, requestId } from "./session-api";
import { useAuthSession } from "./use-auth-session";
import { STANDALONE, type GamePlatform } from "./platform-launch";
import { useStationActions } from "./use-station-actions";
import { regionRisk } from "@frontier/content/risk";
import type { RiskPrivateView } from "@frontier/content/risk";
import { withDiscoveredLoot } from "./private-loot";
import { noticeMessage } from "./notice-message";
import { useGameNotice } from "./use-game-notice";
import { EventJoin } from "./event-join";
import { flightInteraction } from "./flight-interaction";
import { useMissionFocus } from "./use-mission-focus";
import { useArsenalActions } from "./use-arsenal-actions";
import { ProfileSync } from "./profile-sync";
import { chatPacketSchema } from "@frontier/protocol/chat-contracts";
import type { ChatPacket } from "@frontier/protocol/chat-contracts";
import { useChatSession } from "./use-chat-session";
import { usePilotName } from "./use-pilot-name";
import { useFeedback } from "./use-feedback";
import { RoomDepartures } from "./room-departures";

export function useGameSession(platform: GamePlatform = STANDALONE) {
  const { chat, chatController } = useChatSession();
  const [receivedWorld, setReceivedWorld] = useState<{
    snapshot: WorldSnapshot;
    receivedAt: number;
  } | null>(null);
  const publicWorld = receivedWorld?.snapshot ?? null;
  const worldReceivedAt = receivedWorld?.receivedAt ?? 0;
  const [profile, setProfile] = useState<Profile | null>(null);
  const profileRef = useRef<Profile | null>(null);
  profileRef.current = profile;
  const previousLeave = useRef<Promise<void>>(Promise.resolve());
  const [roomDepartures] = useState(() => new RoomDepartures());
  const [risk, setRisk] = useState<RiskPrivateView | null>(null);
  const [discoveries, setDiscoveries] = useState<DiscoveriesView | null>(null);
  const [receivedMissions, setMissions] = useState<MissionView[]>([]);
  const missions = useMemo(
    () => receivedMissions.filter((entry) => entry.runId === profile?.runId),
    [receivedMissions, profile?.runId],
  );
  const missionsRef = useRef(missions);
  missionsRef.current = missions;
  const [events, setEvents] = useState<PublicEventsView>({
    trackedEventId: null,
    participation: [],
  });
  const eventsRef = useRef(events);
  eventsRef.current = events;
  const { mission, focusContract, focusEvent, navigationSource } = useMissionFocus(
    profile, missions, events.trackedEventId,
  );
  const [party, setParty] = useState<PartyView>({
    party: null,
    invitations: [],
    availablePilots: [],
  });
  const [status, setStatus] = useState<
    "connecting" | "connected" | "reconnecting" | "error" | "auth-required"
  >("connecting");
  const { notice, setNotice } = useGameNotice();
  const [busy, setBusy] = useState(false);
  const [profileSync] = useState(
    () =>
      new ProfileSync(
        async (token, signal) => {
          try {
            return await api.profile(token, signal);
          } catch (error) {
            if (
              !signal.aborted &&
              token === tokenRef.current &&
              error instanceof ApiError &&
              error.status === 401
            )
              authController.expire(
                "Сессия истекла. Войди снова или повтори подключение.",
              );
            throw error;
          }
        },
        (next) => {
          authController.observeNickname(tokenRef.current, next);
          setProfile(next);
        },
        setNotice,
      ),
  );
  const roomRef = useRef<Room<SectorState> | null>(null);
  const connectionReady = useRef(false);
  const [eventJoin] = useState(
    () =>
      new EventJoin((type, targetId) => {
        roomRef.current?.send("action", {
          type,
          requestId: requestId(),
          ...(targetId === undefined ? {} : { targetId }),
        });
      }, setNotice),
  );
  const tokenRef = useRef("");
  const contractHistory = useContractHistory(profile, tokenRef, profileSync);
  const achievements = useAchievements(profile, tokenRef, profileSync);
  const season = useSeasonActions(profile, tokenRef, profileSync, setBusy, setNotice);
  const shop = useShopActions(profile, platform, tokenRef, profileSync, setNotice);
  const supplies = useSupplyActions(profile, tokenRef, roomRef, profileSync, status === "connected", setBusy, setNotice);
  const supplyHandlers = useRef(supplies);
  supplyHandlers.current = supplies;
  const arsenal = useArsenalActions(
    profile,
    tokenRef,
    profileSync,
    setBusy,
    setNotice,
    (message) => authController.expire(message),
  );
  const sequence = useRef(0);
  const worldRef = useRef<WorldSnapshot | null>(null);
  const publicWorldRef = useRef<WorldSnapshot | null>(null);
  const playerRef = useRef("");
  const world = useMemo(
    () => withDiscoveredLoot(publicWorld, discoveries, profile?.runId),
    [publicWorld, discoveries, profile?.runId],
  );
  worldRef.current = world;
  const resetSession = useCallback(() => {
    connectionReady.current = false;
    const previous = roomRef.current;
    roomRef.current = null;
    if (previous) {
      try {
        const pilot = worldRef.current?.players.find(item => item.id === playerRef.current);
        const neutral: ClientInput = {
          seq: sequence.current++,
          ...neutralFlightInput(pilot),
        };
        previous.send("input", neutral);
      } catch (error) {
        console.warn("session-neutral-failed", {
          message: error instanceof Error ? error.message : "Connection closed",
        });
      }
      void roomDepartures
        .leave(previous)
        .catch((error: Error) =>
          console.warn("session-disconnect-failed", { message: error.message }),
        );
    }
    profileSync.reset();
    chatController.reset();
    eventJoin.cancel();
    tokenRef.current = "";
    playerRef.current = "";
    worldRef.current = null;
    publicWorldRef.current = null;
    setProfile(null);
    setMissions([]);
    missionsRef.current = [];
    setDiscoveries(null);
    setRisk(null);
    setEvents({ trackedEventId: null, participation: [] });
    eventsRef.current = { trackedEventId: null, participation: [] };
    setParty({ party: null, invitations: [], availablePilots: [] });
    setNotice("");
    setReceivedWorld(null);
    setBusy(false);
    setStatus("auth-required");
  }, [profileSync, eventJoin, chatController, roomDepartures]);
  const { auth, authController, continuePlatformLogin } = useAuthSession(
    resetSession,
    platform,
  );
  const eventContext = useCallback(
    () => ({
      connected: connectionReady.current,
      pilot: publicWorldRef.current?.players.find(
        (item) => item.id === playerRef.current,
      ),
      events: publicWorldRef.current?.publicEvents ?? [],
      trackedEventId: eventsRef.current.trackedEventId,
    }),
    [],
  );

  const routingProfile = profile?.playerId === auth.session?.profile.playerId ? profile : auth.session?.profile;
  const connectionLocation = routingProfile?.tutorial?.version === 2 && !routingProfile.tutorial.docked
    ? routingProfile.personalLocationId ?? "unavailable" : null;
  useEffect(() => {
    if (!auth.session) {
      setStatus(auth.status === "loading" ? "connecting" : "auth-required");
      return;
    }
    if (
      auth.session.profile.factionId === null && auth.session.profile.tutorial?.route !== "training"
    ) {
      tokenRef.current = auth.session.token;
      profileSync.start(auth.session.profile, auth.session.token);
      setStatus("auth-required");
      return;
    }
    const session = auth.session;
    let cancelled = false;
    profileSync.reset();
    setBusy(false);
    let joined: Room<SectorState> | null = null;
    let joining: Promise<Room<SectorState>> | null = null;
    connectionReady.current = false;
    eventJoin.cancel();
    publicWorldRef.current = null;
    setReceivedWorld(null);
    setStatus("connecting");
    setMissions([]);
    setDiscoveries(null);
    setRisk(null);
    setEvents({ trackedEventId: null, participation: [] });
    setParty({ party: null, invitations: [], availablePilots: [] });
    const connect = async () => {
      await previousLeave.current;
      if (cancelled) return;
      tokenRef.current = session.token;
      playerRef.current = session.profile.playerId;
      const seed = profileRef.current?.playerId === session.profile.playerId ? profileRef.current : session.profile;
      profileSync.start(seed, session.token);
      await profileSync.refresh();
      if (cancelled) return;
      const currentProfile = profileSync.current();
      if (!currentProfile) throw new Error("Не удалось подтвердить состояние корабля.");
      const locationId = activePersonalLocation(currentProfile);
      if (locationId !== connectionLocation) return;
      const client = new Client(
        `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}`,
      );
      joining = client.joinOrCreate<SectorState>(locationId ? PERSONAL_ROOM_NAME : ROOM_NAME, {
        protocolVersion: PROTOCOL_VERSION,
        token: session.token,
        ...(locationId ? { locationId } : {}),
      });
      const room = await joining;
      joined = room;
      roomDepartures.track(room);
      if (cancelled) {
        return;
      }
      roomRef.current = room;
      const current = () =>
        !cancelled &&
        roomRef.current === room &&
        tokenRef.current === session.token;
      const connectChat = () =>
        chatController.connect(session.profile.playerId, (command) => {
          if (!current() || !connectionReady.current)
            throw new Error("Chat disconnected");
          room.send("chat", command);
        });
      let chatConnection = connectChat();
      room.onMessage<ChatPacket>("chat", (value) => {
        if (!current()) return;
        const parsed = chatPacketSchema.safeParse(value);
        if (parsed.success) chatConnection.receive(parsed.data);
        else
          console.warn("chat-packet-invalid", {
            issues: parsed.error.issues.length,
          });
      });
      sequence.current = 0;
      room.onStateChange((state) => {
        if (!current()) return;
        const receivedAt = performance.now();
        const snapshot = toWorldSnapshot(state, session.profile.playerId);
        publicWorldRef.current = snapshot;
        eventJoin.receive(eventContext());
        setReceivedWorld({ snapshot, receivedAt });
      });
      room.onMessage<Profile>("profile", (value) => {
        if (current()) void profileSync.invalidate(profileSchema.parse(value));
      });
      room.onMessage<MissionView[]>("missions", (value) => {
        if (current()) setMissions(missionsViewSchema.parse(value));
      });
      room.onMessage<DiscoveriesView>("discoveries", (value) => {
        if (current()) setDiscoveries(discoveriesViewSchema.parse(value));
      });
      room.onMessage<PublicEventsView>("events", (value) => {
        if (!current()) return;
        eventsRef.current = publicEventsViewSchema.parse(value);
        eventJoin.receive(eventContext());
        setEvents(eventsRef.current);
      });
      room.onMessage<SupplyControls>("supplyControls", value => { if (current()) supplyHandlers.current.receiveSupplyControls(value); });
      room.onMessage<SupplyUseAck>("supplyUseAck", value => { if (current()) supplyHandlers.current.receiveSupplyAck(value); });
      room.onMessage<RiskPrivateView>("risk", (value) => {
        if (current()) setRisk(riskPrivateViewSchema.parse(value));
      });
      room.onMessage<PartyView>("party", (value) => {
        if (current()) setParty(partyViewSchema.parse(value));
      });
      room.onMessage<{ message: string; code: string }>("notice", (value) => {
        if (!current()) return;
        const notice = noticeSchema.parse(value);
        eventJoin.reject(notice.code);
        setNotice(noticeMessage(notice));
      });
      room.onError((_code, message) => {
        if (!current()) return;
        eventJoin.cancel();
        setNotice(message ?? "Ошибка связи с сектором");
      });
      room.onDrop(() => {
        if (!current()) return;
        chatConnection.disconnect();
        profileSync.cancelReads();
        connectionReady.current = false;
        setRisk(null);
        eventJoin.cancel();
        if (!cancelled) setStatus("reconnecting");
      });
      room.onReconnect(() => {
        if (current()) {
          chatConnection = connectChat();
          void profileSync.refresh();
          connectionReady.current = true;
          room.send("syncMissions", {});
          room.send("syncParty", {});
          room.send("syncEvents", {});
          room.send("syncRisk", {});
          room.send("syncChat", {});
          setStatus("connected");
        }
      });
      room.onLeave((code) => {
        if (!current()) return;
        if (code === 4003) {
          authController.expire(
            "Сессия не подтверждена. Войди снова или повтори подключение.",
          );
          return;
        }
        profileSync.cancelReads();
        chatConnection.disconnect();
        connectionReady.current = false;
        eventJoin.cancel();
        if (!cancelled) {
          roomRef.current = null;
          setStatus("error");
          setNotice(
            "Связь с сектором прервана. Переподключись, чтобы продолжить вылет.",
          );
        }
      });
      connectionReady.current = true;
      room.send("syncMissions", {});
      room.send("syncParty", {});
      room.send("syncEvents", {});
      room.send("syncRisk", {});
      room.send("syncChat", {});
      setStatus("connected");
    };
    void connect().catch(async (error: Error) => {
      if (!cancelled) {
        try {
          await profileSync.refresh();
          if (cancelled) return;
          const latest = profileSync.current();
          if (latest && activePersonalLocation(latest) !== connectionLocation) return;
        } catch (refreshError) {
          console.warn("world-location-refresh-failed", { message: refreshError instanceof Error ? refreshError.message : "Profile unavailable" });
        }
      }
      if (!cancelled) {
        setNotice(error.message);
        setStatus("error");
      }
    });
    return () => {
      cancelled = true;
      const pendingRoom = joined ? Promise.resolve(joined) : joining;
      const lastPilot = publicWorldRef.current?.players.find(pilot => pilot.id === playerRef.current);
      if (pendingRoom) previousLeave.current = pendingRoom.then(async room => {
        try { room.send("input", { seq: ++sequence.current, ...neutralFlightInput(lastPilot) }); }
        catch (error) { console.warn("world-neutral-input-failed", { message: error instanceof Error ? error.message : "Disconnected" }); }
        await roomDepartures.leave(room);
      }).catch((error: Error) => { console.warn("world-room-leave-failed", { message: error.message }); });
      profileSync.reset();
      chatController.reset();
      connectionReady.current = false;
      eventJoin.cancel();
      roomRef.current = null;
    };
  }, [
    auth.session,
    auth.status,
    authController,
    platform.kind,
    eventJoin,
    eventContext,
    profileSync,
    chatController,
    connectionLocation,
    roomDepartures,
  ]);

  const sendInput = useCallback((intent: InputIntent) => {
    if (connectionReady.current)
      roomRef.current?.send("input", { ...intent, seq: sequence.current++ });
  }, []);
  const action = useCallback(
    (
      type: ClientAction["type"],
      slot?: number,
      targetId?: string,
      riskAcknowledged?: boolean,
    ) => {
      if (type === "launch" || type === "dock" || type === "jump")
        eventJoin.cancel();
      roomRef.current?.send("action", {
        type,
        requestId: requestId(),
        ...(slot === undefined ? {} : { slot }),
        ...(riskAcknowledged === undefined ? {} : { riskAcknowledged }),
        ...(targetId === undefined ? {} : { targetId }),
      });
    },
    [eventJoin],
  );
  const joinEvent = useCallback(
    (eventId: string) => {
      if (eventJoin.request(eventId, eventContext())) focusEvent(eventId);
    },
    [eventJoin, eventContext, focusEvent],
  );
  const interact = useCallback(() => {
    const snapshot = worldRef.current;
    const pilot = snapshot?.players.find(
      (item) => item.id === playerRef.current,
    );
    if (!snapshot || !pilot) return;
    const interaction = flightInteraction(
      snapshot,
      pilot,
      missionsRef.current,
      eventsRef.current,
    );
    if (interaction)
      action(interaction.type, undefined, interaction.targetId, interaction.riskAcknowledged);
    else if (!pilot.docked && !pilot.dead && !pilot.pending)
      setNotice("Подлети ближе к цели, переходу или станции");
  }, [action]);
  const ability = useCallback(
    (slot: number) => action("ability", slot),
    [action],
  );
  const partyCommand = useCallback(
    (command: PartyCommand) => {
      if (status === "connected") roomRef.current?.send("party", command);
    },
    [status],
  );
  const station = useStationActions(
    profile,
    tokenRef,
    profileSync,
    setBusy,
    setNotice,
    (message) => authController.expire(message),
  );
  const renameProfile = usePilotName(
    profile,
    tokenRef,
    profileSync,
    roomRef,
    busy,
    setBusy,
    setNotice,
    (message) => authController.expire(message),
    (token, updated) => authController.observeNickname(token, updated),
  );
  const feedback = useFeedback(tokenRef, profileSync, setNotice, (message) =>
    authController.expire(message),
  );
  const retry = useCallback(
    () => void authController.retry(),
    [authController],
  );
  return {
    auth,
    authController,
    continuePlatformLogin,
    chat,
    chatController,
    world,
    worldReceivedAt,
    risk,
    profile,
    contractHistory,
    achievements,
    mission,
    missions,
    focusContract,
    navigationSource,
    events,
    party,
    partyCommand,
    status,
    notice,
    setNotice,
    busy,
    sendInput,
    action,
    joinEvent,
    interact,
    ability,
    ...supplies,
    ...season,
    ...shop,
    ...station,
    ...arsenal,
    feedback,
    renameProfile,
    retry,
  };
}
